import { Link } from "react-router-dom";
import { companyIntro, honors, milestones, team } from "@/mocks/company";
import { services } from "@/mocks/services";
import { useSEO } from "@/hooks/useSEO";
import SectionHeading from "@/components/base/SectionHeading";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function AboutPage() {
  useSEO({
    title: "关于我们 - 星光传媒影视平台",
    description:
      "了解星光传媒的发展历程、主营业务、团队与资质荣誉。我们专注影视内容制作与传播，让每一个好故事都被看见。",
    keywords: "星光传媒,关于我们,影视公司,发展历程,传媒服务,团队介绍",
  });

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-foreground-950">
        <img
          src="https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20a%20modern%20media%20production%20studio%20interior%20with%20warm%20lighting%20and%20deep%20blue%20shadows%2C%20professional%20atmospheric%20mood%2C%20high%20detail%2C%20abstract%20creative%20workspace&width=1600&height=700&seq=about-hero&orientation=landscape"
          alt="星光传媒制作中心"
          className="absolute inset-0 h-full w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/80 to-foreground-950/60" />
        <div className="relative mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
          <Breadcrumb tone="light" items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-5 font-heading text-3xl font-bold text-background-50 md:text-4xl">
            关于星光传媒
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-300 md:text-base">
            {companyIntro.slogan}。我们以电影、电视剧、综艺等多元内容为载体，致力于为观众带来有温度、
            有品质的作品，为合作伙伴提供专业、可靠的内容服务。
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-accent-600">
              <span className="h-3.5 w-1 rounded-full bg-accent-500" />
              About Us
            </span>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              一家专注内容与创作的影视传媒机构
            </h2>
            <p className="mt-5 text-sm leading-8 text-foreground-700 md:text-base">
              {companyIntro.intro}
            </p>
            <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
              {companyIntro.intro2}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                合作咨询
              </Link>
              <Link
                to="/category/movie"
                className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
              >
                浏览作品
              </Link>
            </div>
          </div>
          <div className="overflow-hidden rounded-lg">
            <img
              src="https://readdy.ai/api/search-image?query=Professional%20creative%20team%20discussing%20a%20film%20project%20in%20a%20modern%20studio%20meeting%20room%2C%20warm%20natural%20lighting%20with%20deep%20blue%20tones%2C%20clean%20editorial%20photography%2C%20high%20detail&width=1000&height=750&seq=about-intro&orientation=landscape"
              alt="星光传媒团队创作讨论"
              title="星光传媒 - 专业影视创作团队"
              className="aspect-[4/3] w-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-primary-950">
        <div className="mx-auto grid w-full max-w-[1280px] grid-cols-2 gap-6 px-4 py-10 md:grid-cols-4 md:px-6 md:py-12">
          {companyIntro.stats.map((item) => (
            <div key={item.label}>
              <p className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
                {item.value}
              </p>
              <p className="mt-1 text-xs text-background-300 md:text-sm">{item.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Business */}
      <section className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="Business"
          title="主营业务"
          desc="围绕影视内容的全链路服务能力，覆盖从创意策划到宣传发行的各个环节。"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
          {services.map((service) => (
            <div
              key={service.id}
              className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                <i className={`${service.icon} text-xl`} />
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-950">
                  {service.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                  {service.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Milestones */}
      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <SectionHeading
            eyebrow="History"
            title="发展历程"
            desc="从成立到成长为综合型传媒机构，星光传媒始终坚持内容为本、创作立身。"
          />
          <div className="relative">
            <div className="absolute left-[7px] top-2 hidden h-[calc(100%-16px)] w-px bg-background-300 md:block" />
            <ol className="flex flex-col gap-6">
              {milestones.map((item) => (
                <li key={item.year} className="relative flex gap-5 md:pl-0">
                  <span className="mt-1.5 hidden h-4 w-4 shrink-0 rounded-full border-2 border-accent-500 bg-background-50 md:block" />
                  <div className="flex-1 rounded-lg border border-background-200 bg-background-50 p-5">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-heading text-lg font-bold text-accent-600">
                        {item.year}
                      </span>
                      <h3 className="font-heading text-base font-semibold text-foreground-950">
                        {item.title}
                      </h3>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                      {item.desc}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="Team"
          title="核心团队"
          desc="经验丰富的导演、制片与内容团队，是星光传媒持续产出优质内容的底气。"
        />
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
          {team.map((member) => (
            <div key={member.id} className="group">
              <div className="aspect-[5/6] w-full overflow-hidden rounded-lg bg-secondary-100">
                <img
                  src={member.avatar}
                  alt={member.name}
                  title={`${member.name} - ${member.role} - 星光传媒`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">
                {member.name}
              </h3>
              <p className="text-xs text-accent-600">{member.role}</p>
              <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                {member.bio}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Honors */}
      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <SectionHeading
            eyebrow="Honors"
            title="资质荣誉"
            desc="多年来，星光传媒的作品与团队获得了来自行业与市场的肯定。"
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
            {honors.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-award-line text-xl" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-0.5 text-xs text-foreground-500">{item.year} 年度</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}